import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import GlobalVariable from '../../GlobalVariable';
import api from '../../api';

export type TestQuestion = {
    id: number;
    text: string;
    type: string;
    answers: {id: number; text: string}[];
};

type AttemptAnswer = {questionId: number; answerId?: number; textAnswer?: string};
type TestResult = {correct: number; total: number; percent: number; coinsAwarded?: number};

export default function LessonTest({lessonId, questions, backUrl}: {lessonId: number; questions: TestQuestion[]; backUrl: string}) {
    const [attemptId, setAttemptId] = useState<number | null>(null);
    const [answers, setAnswers] = useState<Record<number, string | number>>({});
    const [step, setStep] = useState(0);
    const [result, setResult] = useState<TestResult | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [restartKey, setRestartKey] = useState(0);

    useEffect(() => {
        let active = true;
        setAttemptId(null);
        setAnswers({});
        setStep(0);
        setResult(null);
        setError('');
        setLoading(true);

        api.post(GlobalVariable.staffBack() + '/api/test/attempt/start', {lessonId}, {withCredentials: true})
            .then(response => {
                if (!active) return;
                const saved: Record<number, string | number> = {};
                (response.data.answers || []).forEach((answer: AttemptAnswer) => {
                    if (answer.answerId !== undefined && answer.answerId !== null) saved[answer.questionId] = answer.answerId;
                    else if (answer.textAnswer) saved[answer.questionId] = answer.textAnswer;
                });
                setAnswers(saved);
                const firstUnanswered = questions.findIndex(question => saved[question.id] === undefined);
                setStep(firstUnanswered < 0 ? Math.max(questions.length - 1, 0) : firstUnanswered);
                setAttemptId(response.data.attemptId);
                setLoading(false);
            }).catch(() => {
                if (!active) return;
                setError('Не удалось начать тест. Попробуйте ещё раз.');
                setLoading(false);
            });
        return () => { active = false; };
    }, [lessonId, questions, restartKey]);

    if (loading) return <div className='learning-state' role='status'>Подготавливаем тест…</div>;
    if (attemptId === null) return <div className='learning-state is-error' role='alert'>{error}<button type='button' onClick={() => setRestartKey(value => value + 1)}>Повторить</button></div>;

    if (result) {
        const passed = result.percent >= 80;
        return <div className='learning-result'>
            <div className={`learning-result-score ${passed ? 'is-passed' : ''}`}>{result.correct}<span>из {result.total}</span></div>
            <h2>{passed ? 'Тест пройден' : 'Можно попробовать ещё раз'}</h2>
            <p>{passed ? 'Отличный результат. Ваши ответы сохранены.' : 'Вернитесь к материалу урока и пройдите тест повторно, когда будете готовы.'}</p>
            {Number(result.coinsAwarded) > 0 && <div className='learning-reward'>+{result.coinsAwarded} ЧиоКоинов начислено</div>}
            {passed && !Number(result.coinsAwarded) && <p className='learning-no-reward'>За эту попытку новые ЧиоКоины не начислены.</p>}
            <div className='learning-result-actions'>
                <Link to={backUrl} className='learning-secondary-action'>К уроку</Link>
                {!passed && <button type='button' className='learning-primary-action' onClick={() => setRestartKey(value => value + 1)}>Пройти ещё раз</button>}
            </div>
        </div>;
    }

    const question = questions[step];
    if (!question) return <div className='learning-state'>В этом уроке пока нет вопросов.</div>;
    const answer = answers[question.id];
    const answered = typeof answer === 'number' || (typeof answer === 'string' && answer.trim() !== '');

    const advance = async () => {
        if (!answered || saving) return;
        setSaving(true);
        setError('');
        let answerSaved = false;
        try {
            await api.post(GlobalVariable.staffBack() + '/api/test/attempt/answer', {
                attemptId,
                questionId: question.id,
                answerId: question.type === 'choice' ? answer : undefined,
                textAnswer: question.type === 'text' ? answer : undefined,
            }, {withCredentials: true});
            answerSaved = true;

            if (step < questions.length - 1) {
                setStep(value => value + 1);
            } else {
                const response = await api.post(GlobalVariable.staffBack() + '/api/test/attempt/finish', {attemptId}, {withCredentials: true});
                setResult(response.data);
            }
        } catch (requestError) {
            setError(answerSaved
                ? 'Ответ сохранён, но не удалось завершить тест. Попробуйте ещё раз.'
                : 'Не удалось сохранить ответ. Проверьте соединение и попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    return <div className='learning-test-card'>
        <div className='learning-test-progress-head'><span>Вопрос {step + 1} из {questions.length}</span><strong>{Math.round(step / questions.length * 100)}%</strong></div>
        <div className='learning-progress-track' role='progressbar' aria-valuenow={step} aria-valuemin={0} aria-valuemax={questions.length} aria-label='Прогресс теста'>
            <span style={{width: `${Math.round(step / questions.length * 100)}%`}}/>
        </div>
        <h2>{question.text}</h2>
        {question.type === 'choice' ? <div className='learning-answer-list'>
            {question.answers.map(option => <label key={option.id} className={`learning-answer ${answer === option.id ? 'is-selected' : ''}`}>
                <input type='radio' name={`question-${question.id}`} checked={answer === option.id} onChange={() => {setAnswers(previous => ({...previous, [question.id]: option.id})); setError('');}}/>
                <span>{option.text}</span>
            </label>)}
        </div> : <label className='learning-text-answer'>Ваш ответ
            <textarea value={typeof answer === 'string' ? answer : ''} onChange={event => {setAnswers(previous => ({...previous, [question.id]: event.target.value})); setError('');}} placeholder='Напишите ответ'/>
        </label>}
        {error && <div className='learning-test-error' role='alert'>{error}</div>}
        <p className='learning-save-note'>Ответ сохранится при переходе к следующему вопросу.</p>
        <div className='learning-test-actions'>
            <button type='button' className='learning-secondary-action' disabled={step === 0 || saving} onClick={() => {setStep(value => value - 1); setError('');}}>Назад</button>
            <button type='button' className='learning-primary-action' disabled={!answered || saving} onClick={advance}>{saving ? 'Сохраняем…' : step === questions.length - 1 ? 'Завершить тест' : 'Далее'}</button>
        </div>
    </div>;
}
